/** Developers page: API keys, IP allowlists, webhook endpoint, signing secret, webhook deliveries. */
export const developers = {
  title: "Developers",
  intro: "API keys sign your server's requests; the webhook tells your server when deposits and payouts change.",
  introReadOnly: " Some of these settings are read-only for your role.",

  // Integration card (API or hosted payment page)
  integrationTitle: "Integration",
  integrationSub: "How your players pay. You can switch at any time; deposits are credited the same way.",
  integrationApi: "API (your own cashier page)",
  integrationApiBody: "Your site shows each player's deposit address from <code>POST /v1/addresses</code>.",
  integrationHosted: "iCryptoNow payment page",
  integrationHostedBody: "Your server creates a session with <code>POST /v1/checkout-sessions</code> and sends the player to our page: network choice, address and QR code, minimum amount and live status, then back to your site.",
  integrationCurrent: "Current",
  integrationUse: "Use this",
  integrationConfirmTitle: "Switch to: {name}",
  integrationConfirmHosted: "Payment links from POST /v1/checkout-sessions start working right away.",
  integrationConfirmApi: "Open payment links stop working. Deposit addresses keep working and deposits are still credited.",
  integrationSaved: "Integration updated",
  integrationDocs: "How it works",

  // API keys card
  keysTitle: "API keys",
  keysSub: "Each key works only from the IPs you list. Revoke a key the moment you think it leaked.",
  createKey: "Create API key",
  colKeyId: "Key ID",
  colIpAllowlist: "IP allowlist",
  colAccess: "Access",
  colCreated: "Created",
  colLastUsed: "Last used",
  colActions: "Actions",
  anyIp: "Any IP",
  anyIpTitle: "This read-only key works from any network. Add your servers' IPs so a leaked key can't be used elsewhere.",
  accessPayouts: "Payouts",
  accessReadOnly: "Read only",
  accessPayoutsTitle: "Can create payouts, deposit addresses and read data",
  accessReadOnlyTitle: "Deposit addresses and read-only data; cannot create payouts",
  createdBy: "by {name}",
  revokedAt: "Revoked {time}",
  editIps: "Edit IPs",
  editIpsBlocked: "Only people who can create payouts may change the IPs of a payout key",
  revoke: "Revoke",
  noKeys: "No API keys yet",
  noKeysBody: "Create a key to start calling the API from your server.",
  ipsUpdated: "Updated. The API uses the new IPs within 30 seconds.",
  revokeTitle: "Revoke API key?",
  revokeDesc: "Requests signed with <code>{keyId}</code> will fail immediately. This can't be undone.",
  revokeConfirm: "Revoke key",
  keyRevoked: "API key revoked",

  // Shared form messages
  enterCode: "Enter the 6-digit code from your authenticator app.",
  codeFailed: "That code didn't work. Wait for the next code and try again.",
  entries: { one: "{n} entry.", other: "{n} entries." },

  // Create key dialog
  newKeyTitle: "Your new API key",
  createKeyDesc: "The key will only accept requests from these addresses.",
  keyIdLabel: "Key ID",
  secretLabel: "Secret",
  accessPayoutLine: "Access: <b>can create payouts</b> (also deposit addresses and reads).",
  accessReadOnlyLine: "Access: <b>read only</b> (deposit addresses and reads; no payouts).",
  storedSecret: "I've stored the secret somewhere safe (e.g. a secrets manager)",
  serverIps: "Server IPs",
  requiredPayoutKeys: "required for payout keys",
  optionalReadOnlyKeys: "optional for read-only keys",
  ipsHint: "Your servers' public IPs, one per line (IPv4/IPv6 or CIDR).",
  emptyAnyNetwork: "Empty = any network (not recommended).",
  payoutNeedsIp: "A key that can send payouts needs at least one server IP.",
  allowPayouts: "Allow this key to create payouts",
  allowPayoutsHint:
    "Leave it off unless this server sends withdrawals. A read-only key can still create deposit addresses and read deposits, payouts and balances.",
  allowPayoutsBlocked:
    "Only owners and finance members can create keys that send payouts. This key will be read-only: it can create deposit addresses and read data.",
  createKeyBtn: "Create key",

  // Edit key IPs dialog
  editIpsTitle: "Edit server IPs",
  editIpsDesc: "Key <code>{keyId}</code>. The key id and secret don't change; the API uses the new list within 30 seconds.",
  requiredPayoutKey: "required: payout key",
  optionalReadOnlyKey: "optional: read-only key",
  payoutKeepIp: "A key that can send payouts must keep at least one server IP.",
  onePerLine: "One per line (IPv4/IPv6 or CIDR).",
  emptyReadOnlyAnyNetwork: "Empty = this read-only key works from any network (not recommended).",
  yourIp:
    "Your current IP: <code>{ip}</code>. This is the network you're browsing from, usually not your server, so it isn't added for you.",
  saveIps: "Save IPs",

  // Webhook endpoint card
  webhookTitle: "Webhook endpoint",
  webhookSub: "We POST signed events here and retry for 24 hours if your server doesn't answer 2xx.",
  changeUrl: "Change URL",
  setUrl: "Set URL",
  urlSetAt: "Set {time}",
  noWebhook: "No webhook URL yet. Your server won't hear about deposits and payouts until you set one.",

  // Set webhook dialog
  signingSecretTitle: "Webhook signing secret",
  changeWebhookUrl: "Change webhook URL",
  setWebhookUrl: "Set webhook URL",
  savingNewSecret: "Saving creates a new signing secret.",
  endpointLabel: "Endpoint",
  signingSecretLabel: "Signing secret",
  updatedServer: "I've updated my server with the new secret",
  endpointUrl: "Endpoint URL",
  useHttps: "Use a public https:// URL.",
  oldUrlWarning: "The old URL and secret stop working as soon as you save. Have your server ready to verify the new secret.",
  saveUrl: "Save URL",

  // Deliveries card
  deliveriesTitle: "Webhook deliveries",
  deliveriesSub: "Open an event to see the exact JSON we sent and to send it again.",
  filterByStatus: "Filter by status",
  colEvent: "Event",
  colAttempts: "Attempts",
  colLastError: "Last error",
  noEvents: "No events",
  noEventsBody: "Events appear here as deposits and payouts change status.",

  // Event drawer
  eventTitle: "Webhook event",
  resend: "Resend",
  resent: "Event queued to send again",
  lastError: "Last error",
  eventId: "Event ID",
  attempts: "Attempts",
  created: "Created",
  delivered: "Delivered",
  nextAttempt: "Next attempt",
  payload: "Payload",
  copyJson: "Copy JSON",
  payloadAria: "Event payload JSON",

  // API key secrets (view again with 2FA)
  colSecret: "Secret",
  showSecret: "Show secret",
  showSecretBlocked: "Only people who can create payouts can see the secret of a key that sends payouts.",
  revealTitle: "Secret of {keyId}",
  revealDesc: "Enter your authenticator code to see this key's secret.",
  revealLogged: "Every view is logged and your Owners are notified.",
  revealHidesIn: "Hides in {n} s",
  revealHidden: "Hidden again. Show it again if you still need it.",
  keySecretCopyNow: "Copy it now. Owners can view it again later with 2FA.",
};
